import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import slotInstance, { getSlotContract } from "../../../Service/slotInstance";
import web3 from "../../../Service/web3";
import {
  networkID,
  sumWeiToEther,
  compareWei,
} from "../../../../static/utils";
import {
  Path,
  getNetworkCurrency,
  DEFAULT_CURRENCY,
  lotteryTypeMapper,
  LOTTERY_TYPE_IDS,
} from "../../../../static/Constants";

// Theme & Navigation
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";
import { CommonBreadcrumbs, toast } from "../../../Components/ui";

// Subcomponents
import AllLotteriesHero from "./components/AllLotteriesHero";
import GameTypeShowcase from "./components/GameTypeShowcase";
import AllLotteriesFilters from "./components/AllLotteriesFilters";
import PublicLotteryCard from "./components/PublicLotteryCard";
import AllLotteriesEmptyState from "./components/AllLotteriesEmptyState";

import "./AllLotteries.css";

const NoWinner = "0x0000000000000000000000000000000000000000";

const withTimeout = (promise, ms = 4500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms),
    ),
  ]);
};

/**
 * Normalizes an on-chain slot game from AutobetSlot.getOngoingGames()
 * into a lottery object compatible with the AllLotteries interface.
 */
export const mapSlotGame = (game, index) => {
  const gameId = Number(game.gameId || game[0] || index + 1);
  const reels = Number(game.reels || game[1] || 3);
  const isCrypto = Boolean(game.isCrypto ?? game[2]);
  const minBet = game.minBet || game.ticketPrice || game[3] || "0";
  const active = Boolean(game.active ?? game[9] ?? true);
  const startTime = game.startTime || game[10] || "0";
  const endTime = game.endTime || game[11] || "0";
  const owner = game.owner || game[12] || "";
  const partnerWallet = game.partnerWallet || game[13] || "";
  const miniPrize = game.miniPool || game.miniPrize || game[16] || "0";
  const majorPrize = game.majorPool || game[17] || "0";
  const megaPrize = game.megaPool || game[18] || "0";

  const ticketPriceFormatted = web3?.utils?.fromWei
    ? web3.utils.fromWei(String(minBet), "ether")
    : String(minBet);

  const miniPrizeFormatted = web3?.utils?.fromWei
    ? web3.utils.fromWei(String(miniPrize), "ether")
    : String(miniPrize);

  const majorPrizeFormatted = web3?.utils?.fromWei
    ? web3.utils.fromWei(String(majorPrize), "ether")
    : String(majorPrize);

  const megaPrizeFormatted = web3?.utils?.fromWei
    ? web3.utils.fromWei(String(megaPrize), "ether")
    : String(megaPrize);

  const numericMini = Number(miniPrizeFormatted) || 0;
  const numericMajor = Number(majorPrizeFormatted) || 0;
  const numericMega = Number(megaPrizeFormatted) || 0;

  const totalPrizeEther = (numericMini + numericMajor + numericMega).toFixed(2);
  const lotteryType =
    reels === 5 ? LOTTERY_TYPE_IDS.FIVESLOT : LOTTERY_TYPE_IDS.THREESLOT;

  return {
    lotteryId: Number(gameId),
    gameId: Number(gameId),
    lotteryType,
    name: reels === 5 ? `5-SlotBET #${gameId}` : `3-SlotBET #${gameId}`,
    creator: owner
      ? `${owner.slice(0, 6)}...${owner.slice(-4)}`
      : "AutoBET Official",
    owner,
    ownerAddress: owner,
    partnerAddress: partnerWallet,
    reels,
    active,
    status: 0,
    isCrypto,
    startTime,
    endTime,
    ticketPrice: ticketPriceFormatted,
    entryFee: String(minBet),
    totalPrize: web3?.utils?.toWei
      ? web3.utils.toWei(totalPrizeEther, "ether")
      : String(minBet),
    totalPrizeFormatted: totalPrizeEther,
    slotCategory: isCrypto ? "crypto" : "fiat",
    currency: "BET",
    isSlotGame: true,
    totalLines: reels === 3 ? 5 : 1,
    availableLines: reels === 3 ? 5 : 1,
    lineMultipliers: reels === 3 ? [1, 2, 3, 5, 10] : [1],
    jackpots: {
      mini: {
        amount: numericMini.toFixed(2),
        seedValue: (numericMini * 0.5).toFixed(2),
        contributionPerSpin: (Number(ticketPriceFormatted) * 0.02).toFixed(3),
      },
      major: {
        amount: numericMajor.toFixed(2),
        seedValue: (numericMajor * 0.5).toFixed(2),
        contributionPerSpin: (Number(ticketPriceFormatted) * 0.008).toFixed(3),
      },
      mega: {
        amount: numericMega.toFixed(2),
        seedValue: (numericMega * 0.5).toFixed(2),
        contributionPerSpin: (Number(ticketPriceFormatted) * 0.002).toFixed(3),
      },
    },
  };
};

/**
 * Smart contract active lottery predicate (matches pre-revamp code exactly):
 * - SpinBET (0): status 0 (Open) or 5 (Rollover Open)
 * - MineBET (1): (status 0 or 5) && lotteryId
 * - MegaBET (2): status 0, 3 (Rollover/Buy Phase), or 5 (Rollover Open)
 * - RocketBET (3): status != 2 && (status 0, 3, or 5)
 */
export const isLotteryOpen = (lottery) => {
  if (!lottery) return false;
  const typeId = Number(lottery.lotteryType || 0);
  const statusId = Number(lottery.status || 0);
  if (typeId === 0) {
    return statusId === 0 || statusId === 5;
  }
  if (typeId === 1) {
    return (statusId === 0 || statusId === 5) && Boolean(lottery.lotteryId);
  }
  if (typeId === 2) {
    return statusId === 0 || statusId === 3 || statusId === 5;
  }
  return statusId !== 2 && (statusId === 0 || statusId === 3 || statusId === 5);
};

export const isLotteryRollover = (lottery) => {
  if (!lottery) return false;
  const statusId = Number(lottery.status || 0);
  return (
    statusId === 3 ||
    statusId === 5 ||
    Boolean(lottery.rolloverperct && Number(lottery.rolloverperct) > 0)
  );
};

const AllLotteries = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [networkId, setNetworkId] = useState(networkID);
  const [lotteries, setLotteries] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  // Filter & Search State
  const [activeTab, setActiveTab] = useState("all"); // "all" | "2" (MegaBET) | "0" (SpinBET) | "1" (MineBET) | "3" (RocketBET)
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId],
  );

  /**
   * Fetch All Live & Active On-Chain Lotteries
   */
  const fetchLotteries = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Safety watchdog to guarantee loading finishes
      let watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7000);

      try {
        if (window.ethereum) {
          window.web3 = web3;
          if (window.ethereum.networkVersion) {
            setNetworkId(window.ethereum.networkVersion);
          } else if (web3 && web3.eth && web3.eth.net && web3.eth.net.getId) {
            withTimeout(web3.eth.net.getId(), 2000)
              .then((netId) => {
                if (netId) setNetworkId(netId);
              })
              .catch(() => {});
          }
        }

        // 1. Concurrently fetch traditional lotteries from cinstance
        const fetchTraditional = async () => {
          try {
            const totalIdsRes = await withTimeout(
              cinstance.methods.lotteryId().call(),
              4000,
            ).catch((err) => {
              console.warn("Could not fetch lotteryId():", err);
              return 1;
            });

            const totalIds = Number(totalIdsRes) || 1;
            const lotteryCalls = [];
            for (let i = 1; i < totalIds; i++) {
              lotteryCalls.push(
                withTimeout(cinstance.methods.lottery(i).call(), 4000).catch(
                  () => null,
                ),
              );
            }

            const rawResults = (await Promise.all(lotteryCalls)).filter(
              (item) => item && item.lotteryId && item.lotteryId !== "0",
            );

            return rawResults.filter(isLotteryOpen);
          } catch (tErr) {
            console.warn("Error loading traditional lotteries:", tErr);
            return [];
          }
        };

        // 2. Concurrently fetch 3-Slot and 5-Slot Games from slotInstance
        const fetchSlotGames = async () => {
          try {
            const contract = getSlotContract() || slotInstance;
            if (!contract?.methods?.getOngoingGames) {
              return [];
            }

            const ongoingGames = await withTimeout(
              contract.methods.getOngoingGames().call(),
              4500,
            ).catch((sErr) => {
              console.warn("Could not fetch getOngoingGames():", sErr);
              return [];
            });

            if (!Array.isArray(ongoingGames)) return [];

            return ongoingGames
              .filter((game) => {
                const reels = Number(game.reels || game[1] || 0);
                const active = Boolean(game.active ?? game[9] ?? true);
                return (reels === 3 || reels === 5) && active;
              })
              .map(mapSlotGame);
          } catch (sErr) {
            console.warn("Error loading slot games:", sErr);
            return [];
          }
        };

        const [activeTraditional, slotGames] = await Promise.all([
          fetchTraditional(),
          fetchSlotGames(),
        ]);

        const combinedLotteries = [...activeTraditional, ...slotGames];
        setLotteries(combinedLotteries);
        const activeResults = combinedLotteries;

        // 3. Concurrently fetch partner details for active partners (non-blocking)
        const uniquePartners = [
          ...new Set(
            activeResults
              .map((item) => item.partner || item.partnerAddress)
              .filter((addr) => addr && addr !== NoWinner),
          ),
        ];

        if (uniquePartners.length > 0) {
          Promise.all(
            uniquePartners.map((addr) =>
              withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3500)
                .then((pData) =>
                  pData ? Object.assign({ address: addr }, pData) : null,
                )
                .catch(() => null),
            ),
          ).then((partnerResults) => {
            const map = {};
            partnerResults.filter(Boolean).forEach((p) => {
              map[p.address] = p;
              if (p.partnerAddress) map[p.partnerAddress] = p;
            });
            setPartnerMap(map);
          });
        }

        if (isRefresh) {
          toast.success("Active lotteries refreshed from blockchain!");
        }
      } catch (err) {
        console.error("Error loading active lotteries:", err);
        toast.error("Failed to load on-chain lotteries.");
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  // Load Partner Logos (Optional S3 service)
  useEffect(() => {
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          setPartnerLogoData(
            response && response.data && response.data.result
              ? response.data.result
              : [],
          );
        })
        .catch(() => {
          setPartnerLogoData([]);
        });
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchLotteries();
  }, [fetchLotteries]);

  /**
   * Compute Total Active Counts and Prize Pool
   */
  const { totalActiveCount, totalPrizePool, countsByType } = useMemo(() => {
    const counts = {
      all: lotteries.length,
      [LOTTERY_TYPE_IDS.SPINBET]: 0,
      [LOTTERY_TYPE_IDS.MINEBET]: 0,
      [LOTTERY_TYPE_IDS.MEGABET]: 0,
      [LOTTERY_TYPE_IDS.ROCKETBET]: 0,
      [LOTTERY_TYPE_IDS.THREESLOT]: 0,
      [LOTTERY_TYPE_IDS.FIVESLOT]: 0,
    };
    const prizeList = [];

    lotteries.forEach((lottery) => {
      const typeId = Number(lottery.lotteryType ?? 0);
      if (counts[typeId] !== undefined) {
        counts[typeId] += 1;
      }
      if (lottery.totalPrize) {
        prizeList.push(lottery.totalPrize);
      }
    });

    return {
      totalActiveCount: lotteries.length,
      totalPrizePool: sumWeiToEther(prizeList, 5),
      countsByType: counts,
    };
  }, [lotteries]);

  /**
   * Filter and Sort Active Lotteries for Display
   */
  const filteredLotteries = useMemo(() => {
    return lotteries
      .filter((lottery) => {
        const typeId = String(lottery.lotteryType ?? "0");

        // 1. Game Type Tab Filter
        if (activeTab !== "all" && typeId !== activeTab) {
          return false;
        }

        // 2. Search Query Filter
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const idMatch = String(lottery.lotteryId || lottery.gameId || "").toLowerCase().includes(q);
          const nameMatch = String(lottery.name || "").toLowerCase().includes(q);
          const ownerMatch = (lottery.ownerAddress || lottery.owner || "")
            .toLowerCase()
            .includes(q);
          const typeNameMatch = (
            lotteryTypeMapper[typeId] ||
            (lottery.reels ? `${lottery.reels}-SlotBET` : "")
          )
            .toLowerCase()
            .includes(q);
          const partnerAddr = (
            lottery.partner ||
            lottery.partnerAddress ||
            ""
          ).toLowerCase();
          const partnerMatch = partnerAddr.includes(q);

          if (!idMatch && !nameMatch && !ownerMatch && !typeNameMatch && !partnerMatch) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        const idA = Number(a.lotteryId || a.gameId || 0);
        const idB = Number(b.lotteryId || b.gameId || 0);
        const prizeComp = compareWei(a.totalPrize, b.totalPrize);
        const feeComp = compareWei(a.entryFee, b.entryFee);
        const capA = Number(a.capacity || 0);
        const capB = Number(b.capacity || 0);

        if (sortBy === "oldest") return idA - idB;
        if (sortBy === "highestPrize") return -prizeComp || idB - idA;
        if (sortBy === "lowestPrize") return prizeComp || idA - idB;
        if (sortBy === "entryFee" || sortBy === "lowestEntry") return feeComp || idA - idB;
        if (sortBy === "capacityHigh") return capB - capA || idB - idA;
        if (sortBy === "capacityLow") return capA - capB || idA - idB;
        // Default: newest first
        return idB - idA;
      });
  }, [lotteries, activeTab, searchQuery, sortBy]);

  const handleResetFilters = () => {
    setActiveTab("all");
    setSearchQuery("");
    setSortBy("newest");
  };

  const hasActiveFilters =
    activeTab !== "all" ||
    searchQuery.trim() !== "" ||
    sortBy !== "newest";

  return (
    <Box
      className="all-lotteries-canvas"
      sx={{ backgroundColor: colors.dashboard.canvas }}
    >
      <NavBar />

      <Container maxWidth="xl" sx={{ pt: { xs: "96px", sm: "108px", md: "116px" }, pb: 6, flexGrow: 1 }}>
        {/* Breadcrumb Navigation */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "All Lotteries" },
          ]}
        />


        {/* 1. Hero Section with Live Stats */}
        <AllLotteriesHero
          totalActiveCount={totalActiveCount}
          totalPrizePool={totalPrizePool}
          currency={currency}
        />

        {/* 2. Interactive Game Engine Showcase */}
        <GameTypeShowcase
          countsByType={countsByType}
          activeTab={activeTab}
          onSelectTab={(tabId) => {
            setActiveTab((prev) => (prev === tabId ? "all" : tabId));
          }}
        />

        {/* 3. Category Tabs, Search & Sort Toolbar */}
        <AllLotteriesFilters
          activeTab={activeTab}
          onTabChange={setActiveTab}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          countsByType={countsByType}
          onRefresh={() => fetchLotteries(true)}
          refreshing={refreshing}
        />

        {/* 4. Loading Skeletons */}
        {loading && (
          <Grid container spacing={3}>
            {[1, 2, 3].map((idx) => (
              <Grid item xs={12} sm={6} lg={4} key={idx}>
                <Skeleton
                  variant="rounded"
                  height={380}
                  animation="wave"
                  sx={{
                    bgcolor: colors.slate[200],
                    borderRadius: borderRadiusPx.xl,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* 5. Empty State */}
        {!loading && filteredLotteries.length === 0 && (
          <AllLotteriesEmptyState
            onReset={handleResetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        )}

        {/* 6. Active Lottery Cards Grid */}
        {!loading && filteredLotteries.length > 0 && (
          <Grid container spacing={3}>
            {filteredLotteries.map((lottery) => {
              const partnerAddr = lottery.partner || lottery.partnerAddress;
              const partnerData = partnerMap[partnerAddr] || null;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={4}
                  key={
                    lottery.isSlotGame
                      ? `slot-${lottery.reels}-${lottery.lotteryId || lottery.gameId}`
                      : `lottery-${lottery.lotteryId}`
                  }
                >
                  <PublicLotteryCard
                    lottery={lottery}
                    currency={currency}
                    partner={partnerData}
                    partnerLogoData={partnerLogoData}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default AllLotteries;
